import { useEffect, useState } from 'react';
import type { TProject } from '../../types';
import type { ETaskStatus, TTaskDetails } from '../types';
import TaskFormDialog from './TaskFormDialog';
import {
  Body1,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from '@fluentui/react-components';
import { ClipboardTaskListLtr20Regular, Dismiss24Regular, EditRegular } from '@fluentui/react-icons';
import { StatusLabel } from '@shared/components/StatusLabel';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import { useTask, useUpdateTask } from '../api';
import { TASK_DIALOG_PARAM } from '../constants';
import TaskDetailsOverview from './TaskDetailsOverview';
import TaskStatusMenu from './TaskStatusMenu';

const TaskViewDialog = ({
  project,
  rootItemId,
  taskId,
  statusPending,
  onStatusChange,
}: {
  project: TProject;
  rootItemId: string;
  taskId: string;
  statusPending: boolean;
  onStatusChange: (status: ETaskStatus) => void;
}) => {
  const { change } = useSearchParams();
  const projectId = project._id;
  // Keep the selected task while Fluent animates the closed dialog out.
  const [lastTaskId, setLastTaskId] = useState(taskId);
  useEffect(() => {
    if (taskId) setLastTaskId(taskId);
  }, [taskId]);
  // The edited task stays set after closing, so the form keeps its title while it animates out.
  const [editDetails, setEditDetails] = useState<TTaskDetails>();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const updateTask = useUpdateTask();
  const { data, error } = useTask(taskId || lastTaskId, !!taskId);
  // GET /tasks/:id only takes the task id, so a ?task= link to another Project's task would otherwise open here.
  const unavailable = !!data && data.task.project !== projectId;
  const details = !unavailable ? data : undefined;
  const close = () => change(TASK_DIALOG_PARAM, undefined, { replace: false });
  return (
    <>
      <Dialog
        open={!!taskId && !isEditOpen}
        onOpenChange={(_, data) => {
          if (!data.open) close();
        }}
      >
        <DialogSurface className="!max-w-[960px]">
          <DialogBody>
            <DialogTitle action={<Button appearance="subtle" icon={<Dismiss24Regular />} onClick={close} />}>
              <div className="flex items-center space-x-2">
                <ClipboardTaskListLtr20Regular className="shrink-0" />
                <span className="min-w-0 break-words">{details?.task.title ?? TERMINOLOGY.task.upper}</span>
              </div>
            </DialogTitle>
            <DialogContent className="!overflow-hidden">
              {unavailable ? (
                <Body1>This {TERMINOLOGY.task.lower} is not available.</Body1>
              ) : (
                <StatusLabel
                  data={details}
                  error={error}
                  loadingMessage={`Loading ${TERMINOLOGY.task.lower}...`}
                  errorMessage={`Loading the ${TERMINOLOGY.task.lower} failed!`}
                  render={(taskDetails) => (
                    <div className="flex min-h-[min(320px,60vh)] max-h-[70vh] flex-col gap-5">
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <Button
                          appearance="primary"
                          icon={<EditRegular />}
                          disabled={!taskDetails.permissions.canEdit || statusPending}
                          onClick={() => {
                            setEditDetails(taskDetails);
                            setIsEditOpen(true);
                          }}
                        >
                          Edit details
                        </Button>
                        <TaskStatusMenu
                          status={taskDetails.task.status}
                          disabled={statusPending}
                          onStatusChange={taskDetails.permissions.canEdit ? onStatusChange : undefined}
                        />
                      </div>
                      <div className="flex min-h-0 flex-1 overflow-y-auto">
                        <TaskDetailsOverview
                          task={taskDetails.task}
                          resolvedItems={taskDetails.resolvedItems}
                          due={taskDetails.due}
                        />
                      </div>
                    </div>
                  )}
                />
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <TaskFormDialog
        isOpen={isEditOpen}
        setIsOpen={setIsEditOpen}
        project={project}
        details={editDetails}
        rootItemId={rootItemId}
        onSave={async (_values, changes) => {
          // Back or Forward can change ?task= while the form is open; the save still goes to the edited task.
          await updateTask.mutateAsync({ id: editDetails!.task._id, changes });
        }}
      />
    </>
  );
};
export default TaskViewDialog;
